// Read the signing certificate out of an APK and turn it into the checksum Android's QR
// provisioning expects.
//
// PROVISIONING_DEVICE_ADMIN_SIGNATURE_CHECKSUM is a base64url (no padding) SHA-256 of the DER
// encoded signing certificate - the same bytes `apksigner verify --print-certs` prints as
// "certificate SHA-256 digest", only in another alphabet. During provisioning the device downloads
// the APK and refuses to install it unless the certificate matches, which is what stops anyone from
// pointing a copy of our QR at their own build.
//
// An APK is a zip; the v1 signature block sits in META-INF as a .RSA / .DSA / .EC file, which is a
// PKCS#7 structure with the certificate embedded. We do not parse PKCS#7 properly - we find the
// X.509 certificate inside it, which is enough and keeps this dependency-free. The result is always
// verified against the value the operator can read with apksigner, so a wrong guess cannot pass
// silently: the page shows the checksum and the hex digest side by side.

import JSZip from 'jszip';

/** Locate the DER of the first X.509 certificate inside a PKCS#7 blob. */
function extractCertificateDer(pkcs7) {
    // An X.509 certificate is a SEQUENCE whose first element is another SEQUENCE (tbsCertificate)
    // that starts with the explicit [0] version tag: 30 82 xx xx 30 82 xx xx a0 03 02 01 02.
    for (let i = 0; i < pkcs7.length - 16; i++) {
        if (pkcs7[i] === 0x30 && pkcs7[i + 1] === 0x82 &&
            pkcs7[i + 4] === 0x30 && pkcs7[i + 5] === 0x82 &&
            pkcs7[i + 8] === 0xa0 && pkcs7[i + 9] === 0x03 &&
            pkcs7[i + 10] === 0x02 && pkcs7[i + 11] === 0x01 && pkcs7[i + 12] === 0x02) {
            const len = (pkcs7[i + 2] << 8 | pkcs7[i + 3]) + 4; // header + payload
            if (i + len <= pkcs7.length) return pkcs7.slice(i, i + len);
        }
    }
    return null;
}

const toHex = (buf) => Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0')).join('');

const toBase64Url = (buf) => {
    let bin = '';
    new Uint8Array(buf).forEach((b) => { bin += String.fromCharCode(b); });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * SHA-256 of the whole APK file, base64url - the value for the older
 * PROVISIONING_DEVICE_ADMIN_PACKAGE_CHECKSUM extra.
 *
 * The signature checksum is the modern and preferred check, but it is not universally honoured, and
 * a device that ignores it fails provisioning with the same blank "contact your IT admin" as
 * everything else. Sending both costs nothing at scan time. The catch is that this one pins the
 * exact file, not the key: publishing a new APK invalidates it, so the QR has to be regenerated
 * after every agent release - which is why it stays optional.
 *
 * @param {File} apkFile
 * @returns {Promise<string>}
 */
export async function readApkFileChecksum(apkFile) {
    const buf = await apkFile.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return toBase64Url(digest);
}

/**
 * @param {File} apkFile
 * @returns {Promise<{checksum: string, sha256Hex: string}>}
 * @throws when the APK carries no v1 signature block we can read
 */
export async function readApkSignatureChecksum(apkFile) {
    const zip = await JSZip.loadAsync(apkFile);
    const sigEntry = Object.keys(zip.files).find((n) => /^META-INF\/.*\.(RSA|DSA|EC)$/i.test(n));
    if (!sigEntry) {
        throw new Error(
            'No v1 (JAR) signature found in this APK. The agent must be signed with v1 enabled - ' +
            'QR provisioning reads the certificate from META-INF.');
    }

    const pkcs7 = await zip.file(sigEntry).async('uint8array');
    const der = extractCertificateDer(pkcs7);
    if (!der) throw new Error('Could not locate the certificate inside ' + sigEntry);

    const digest = await crypto.subtle.digest('SHA-256', der);
    return { checksum: toBase64Url(digest), sha256Hex: toHex(digest) };
}
